// Consultation · Stage — Social history.
//
// BUILD-TO-CONTRACT FOLDS / OMISSIONS (no new payload fields were added):
//
//   Smoking            → history.social.smoking          (verbatim label: Never / Ex-smoker / Current)
//   Alcohol use        → history.social.alcohol          (verbatim label: Never / Occasional / Regular)
//   Occupation / notes → history.social.occupationNotes  (free text)
//   Living condition   → history.social.occupationNotes  (FOLD: the contract has no
//                        structured living-condition field. The chosen condition is
//                        stored on its own first line behind a "Living: " prefix; the
//                        rest of `occupationNotes` is the clinician's free text. The
//                        fold round-trips losslessly and never clobbers free text.)
//
//   OMITTED (flagged at the Phase 2 checkpoint):
//     • Sexual history — the design shows a collapsed, sensitive structured block.
//       There is NO structured contract field for it, and folding it into the shared
//       `occupationNotes` free text would mix sensitive data into an unrelated field.
//       It is therefore NOT captured here. A future backend field would restore it.

import { useTranslation } from 'react-i18next';
import type { StageProps } from './types';
import { StagePane } from './StagePane';
import { Field } from '@/components/synapse/Field';
import { Segmented } from '@/components/synapse/Segmented';
import { MultiSelectPills } from '@/components/synapse/MultiSelectPills';
import { NotesTextarea } from '@/components/synapse/NotesTextarea';
import { cn } from '@/lib/utils';
import { LIVING_CONDITIONS } from '@/lib/clinicalStarterLists';

// Living condition is folded onto a single prefixed line of `occupationNotes`.
// To avoid colliding with a clinician who naturally types "Living: ..." prose
// into the housing/occupation notes, a line is treated as the STRUCTURED living
// field ONLY when it exactly equals `Living: <known option>` (the pills can emit
// nothing else). Any other "Living: …" prose is left untouched as free text.
export const LIVING_PREFIX = 'Living: ';

const LIVING_LINES = new Set(LIVING_CONDITIONS.map((c) => `${LIVING_PREFIX}${c}`));

export function readLiving(notes: string | undefined): string | null {
  if (!notes) return null;
  for (const line of notes.split('\n')) {
    if (LIVING_LINES.has(line)) return line.slice(LIVING_PREFIX.length);
  }
  return null;
}

export function readOccupationText(notes: string | undefined): string {
  if (!notes) return '';
  return notes
    .split('\n')
    .filter((line) => !LIVING_LINES.has(line))
    .join('\n');
}

// Rebuild `occupationNotes` from the living-condition line + the free text. Only
// a recognised option is folded in (anything else would be free text, not a
// structured living value).
export function writeOccupationNotes(living: string | null, freeText: string): string | undefined {
  const lines: string[] = [];
  if (living && LIVING_CONDITIONS.includes(living)) lines.push(`${LIVING_PREFIX}${living}`);
  if (freeText.trim()) lines.push(freeText);
  const merged = lines.join('\n');
  return merged === '' ? undefined : merged;
}

const SMOKING_OPTS = [
  { value: 'Never', labelKey: 'consult.social.smokingNever' },
  { value: 'Ex-smoker', labelKey: 'consult.social.smokingEx' },
  { value: 'Current', labelKey: 'consult.social.smokingCurrent' },
] as const;

const ALCOHOL_OPTS = [
  { value: 'Never', labelKey: 'consult.social.alcoholNever' },
  { value: 'Occasional', labelKey: 'consult.social.alcoholOccasional' },
  { value: 'Regular', labelKey: 'consult.social.alcoholRegular' },
] as const;

function PrefilledBadge() {
  const { t } = useTranslation();
  return (
    <span
      className="ml-1.5 rounded-full px-1.5 py-px text-[10px] font-semibold"
      style={{ color: 'var(--success)', background: 'var(--success-bg)' }}
    >
      {t('consult.social.prefilled')}
    </span>
  );
}

export function StageSocial({ history, setHistory, prefilled, readOnly }: StageProps) {
  const { t } = useTranslation();

  const social = history.social ?? {};
  const smoking = social.smoking ?? '';
  const alcohol = social.alcohol ?? '';
  const living = readLiving(social.occupationNotes);
  const occupationText = readOccupationText(social.occupationNotes);
  const socialPrefilled = prefilled.has('social');

  function patchSocial(patch: { smoking?: string; alcohol?: string; occupationNotes?: string }) {
    setHistory((p) => {
      const next = { ...p.social, ...patch };
      // Drop empty-string values so we never persist blank keys.
      for (const k of ['smoking', 'alcohol', 'occupationNotes'] as const) {
        if (next[k] === '' || next[k] == null) delete next[k];
      }
      return { ...p, social: next };
    });
  }

  function setSmoking(v: string) {
    patchSocial({ smoking: v });
  }
  function setAlcohol(v: string) {
    patchSocial({ alcohol: v });
  }
  function setLiving(next: string[]) {
    // Single-choice: take the most recently toggled value (last in the list).
    const chosen = next.length > 0 ? next[next.length - 1]! : null;
    patchSocial({ occupationNotes: writeOccupationNotes(chosen, occupationText) });
  }
  function setOccupationText(v: string) {
    patchSocial({ occupationNotes: writeOccupationNotes(living, v) });
  }

  const livingOptions = LIVING_CONDITIONS.map((v) => ({ value: v, label: v }));

  return (
    <StagePane titleKey="consult.stage.social.label" subKey="consult.stage.social.sub">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          label={
            <>
              {t('consult.social.smoking')}
              {socialPrefilled && <PrefilledBadge />}
            </>
          }
        >
          {readOnly ? (
            <ReadOnlyValue value={smoking || t('common.none')} />
          ) : (
            <Segmented
              value={smoking}
              onChange={setSmoking}
              options={SMOKING_OPTS as unknown as { value: string; labelKey: string }[]}
              ariaLabel={t('consult.social.smoking')}
            />
          )}
        </Field>

        <Field label={t('consult.social.alcohol')}>
          {readOnly ? (
            <ReadOnlyValue value={alcohol || t('common.none')} />
          ) : (
            <Segmented
              value={alcohol}
              onChange={setAlcohol}
              options={ALCOHOL_OPTS as unknown as { value: string; labelKey: string }[]}
              ariaLabel={t('consult.social.alcohol')}
            />
          )}
        </Field>
      </div>

      <Field label={t('consult.social.living')} hint={t('consult.social.livingHint')} optional>
        {readOnly ? (
          <ReadOnlyValue value={living ?? t('common.none')} />
        ) : (
          <MultiSelectPills
            value={living ? [living] : []}
            onChange={setLiving}
            options={livingOptions}
            ariaLabel={t('consult.social.living')}
          />
        )}
      </Field>

      <Field
        label={t('consult.social.occupation')}
        hint={t('consult.social.occupationHint')}
        optional
      >
        {(id) =>
          readOnly ? (
            <ReadOnlyValue id={id} value={occupationText || t('common.none')} multiline />
          ) : (
            <NotesTextarea
              id={id}
              value={occupationText}
              onChange={setOccupationText}
              placeholderKey="consult.social.occupationPlaceholder"
              minRows={2}
            />
          )
        }
      </Field>
    </StagePane>
  );
}

// Static, read-only rendering of a value (finalized consultations are immutable).
function ReadOnlyValue({
  id,
  value,
  multiline,
}: {
  id?: string;
  value: string;
  multiline?: boolean;
}) {
  return (
    <div
      id={id}
      className={cn(
        'rounded-md border border-line bg-paper px-3.5 text-sm text-ink',
        multiline ? 'min-h-11 py-[11px] leading-relaxed' : 'flex h-11 items-center',
      )}
    >
      {value}
    </div>
  );
}
